// KEEPERS for the season ahead (PLAN-2026-10-27-the-next-season §2.3, §5;
// scenario Q7): a team's last-season roster with what each player would
// cost, chosen up to the count, and every team's keepers, which are public as
// they are chosen. A commissioner may choose for any team until the draft.
//
// THE ORDER CHOSEN IS THE ORDER THE EARLIEST PICKS GO IN under a rule that
// puts keepers on the team's first rounds, so the list numbers each choice.
// What a keeper costs and where he lands are the server's (`keeperCost`,
// `placeKeepers`); this screen only shows them.

import { useMemo, useState } from 'react';
import { whenInZone, type KeepersTeam, type KeepersView } from '@fsp/domain';
import { messageOf } from '../data/api';
import { useReaderZone } from '../data/hooks';
import { saveKeepers, useKeepers } from '../data/keepers';
import { Panel } from '../kit/Panel';
import {
  Button,
  Callout,
  Caption,
  Checkbox,
  Choice,
  Line,
  Stack,
  Text,
  Title,
  tell,
} from '../kit/ui';
import { DashboardScreen } from '../templates/DashboardScreen';
import { FromLeague } from './shared/FromLeague';
import { TeamName } from './shared/Names';
import '../styles/screens/keepers.css';

export function KeepersScreen() {
  const keepers = useKeepers();
  return <FromLeague snapshot={keepers}>{(view) => <Keepers view={view} />}</FromLeague>;
}

function Keepers({ view }: { view: KeepersView }) {
  const zone = useReaderZone();
  const editable = view.teams.filter((t) => t.canEdit);
  const mine = view.teams.find((t) => t.team.isMine) ?? null;
  const [editing, setEditing] = useState<string | null>(null);
  const team =
    view.teams.find((t) => t.team.id === editing) ??
    (mine?.canEdit ? mine : null) ??
    editable[0] ??
    mine;

  const when =
    view.deadline === null || zone === null ? null : whenInZone(view.deadline, zone, Date.now());
  const status = view.final
    ? 'The draft has started, so every team’s keepers are set.'
    : when === null
      ? 'Keepers lock an hour before the draft, once it has a time.'
      : `Keepers lock ${when}, an hour before the draft. A commissioner can change them until it starts.`;

  const none =
    view.mode === 'dynasty'
      ? 'In a dynasty every team keeps its whole roster.'
      : view.mode === 'redraft'
        ? 'This league starts every season fresh, so nobody is kept.'
        : view.teams.every((t) => t.players.length === 0)
          ? 'This league has no last season to keep players from.'
          : null;

  return (
    <DashboardScreen
      widgets={[
        {
          id: 'head',
          pinned: true,
          content: (
            <Stack gap={0}>
              <Title>Keepers</Title>
              <Text tone="secondary" size="xs" block>
                {view.mode === 'keeper'
                  ? `Up to ${view.count === 1 ? 'one player' : `${view.count} players`} a team, from last season`
                  : view.league.name}
              </Text>
            </Stack>
          ),
        },
        ...(none !== null
          ? [
              {
                id: 'none',
                column: 1 as const,
                content: (
                  <Panel title="Keepers">
                    <div className="fsp-keepers-note">
                      <Text>{none}</Text>
                    </div>
                  </Panel>
                ),
              },
            ]
          : [
              {
                id: 'choose',
                column: 1 as const,
                content:
                  team === null ? (
                    <Panel title="Your keepers">
                      <div className="fsp-keepers-note">
                        <Text tone="secondary">You have no team in this league.</Text>
                      </div>
                    </Panel>
                  ) : (
                    <Chooser
                      key={team.team.id}
                      view={view}
                      team={team}
                      status={status}
                      editable={editable}
                      onTeam={setEditing}
                    />
                  ),
              },
              {
                id: 'everyone',
                column: 2 as const,
                content: <Everyone view={view} />,
              },
            ]),
      ]}
    />
  );
}

/** One team's last-season roster, chosen up to the count, and saved. */
function Chooser({
  view,
  team,
  status,
  editable,
  onTeam,
}: {
  view: KeepersView;
  team: KeepersTeam;
  status: string;
  editable: KeepersTeam[];
  onTeam: (teamId: string) => void;
}) {
  const [chosen, setChosen] = useState<string[]>(team.chosen);
  const [saving, setSaving] = useState(false);
  const changed = chosen.join() !== team.chosen.join();
  const over = chosen.length > view.count;
  const toggle = (id: string, on: boolean) =>
    setChosen((list) =>
      on ? [...list.filter((x) => x !== id), id] : list.filter((x) => x !== id),
    );
  const save = async () => {
    setSaving(true);
    const reply = await saveKeepers(view.league.id, team.team.id, chosen);
    setSaving(false);
    if (reply.status !== 'ok') {
      tell(
        messageOf(
          reply,
          'That did not work. The keepers were not saved.',
          'Could not reach the site. The keepers were not saved.',
        ),
        'bad',
      );
      return;
    }
    tell(
      chosen.length === 0
        ? `${team.team.name} keeps nobody.`
        : `${team.team.name} keeps ${chosen.length === 1 ? 'one player' : `${chosen.length} players`}.`,
      'good',
    );
  };
  const placed = useMemo(
    () => new Map(team.players.filter((p) => p.kept).map((p) => [p.player.id, p.round])),
    [team],
  );

  return (
    <Panel title={team.team.isMine ? 'Your keepers' : `${team.team.name}’s keepers`}>
      <div className="fsp-keepers">
        <Text tone="secondary" size="xs" block>
          {view.rule}
        </Text>
        <Text size="xs" block>
          {status}
        </Text>
        {editable.length > 1 || (editable.length === 1 && !team.team.isMine) ? (
          <Choice
            label="Choosing for"
            searchable={false}
            required
            value={team.team.id}
            onChange={(v) => v && onTeam(v)}
            options={editable.map((t) => ({ value: t.team.id, label: t.team.name }))}
          />
        ) : null}
        {team.players.length === 0 ? (
          <Text tone="secondary">Nobody was on this team’s roster at the end of last season.</Text>
        ) : (
          <ul className="fsp-keepers-list">
            {team.players.map((p) => {
              const index = chosen.indexOf(p.player.id);
              const round = placed.get(p.player.id) ?? null;
              return (
                <li key={p.player.id} className="fsp-keepers-row">
                  <Checkbox
                    label={
                      <span className="fsp-keepers-name">
                        {index >= 0 ? <span className="fsp-num">{index + 1}. </span> : null}
                        {p.player.name}
                        <Caption>
                          {[p.player.positions[0], p.player.proTeam].filter(Boolean).join(' · ')}
                        </Caption>
                      </span>
                    }
                    description={
                      p.cost === null
                        ? (p.reason ?? 'Cannot be kept.')
                        : [
                            `Costs ${p.cost.label.toLowerCase().replace(/^an early pick$/, 'one of the earliest picks')}`,
                            index >= 0 &&
                            round !== null &&
                            round !== p.cost.round &&
                            changed === false
                              ? `on round ${round}`
                              : null,
                            p.years !== null && p.years > 1
                              ? `kept ${p.years} seasons in a row`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                    }
                    on={index >= 0}
                    disabled={!team.canEdit || p.cost === null}
                    onChange={(on) => toggle(p.player.id, on)}
                  />
                </li>
              );
            })}
          </ul>
        )}
        {team.canEdit ? (
          <Stack gap={2}>
            {over ? (
              <Callout tone="bad">
                {`A team keeps ${view.count === 1 ? 'one player' : `up to ${view.count}`}. Take ${chosen.length - view.count === 1 ? 'one' : String(chosen.length - view.count)} off.`}
              </Callout>
            ) : null}
            <Line gap={2} wrap>
              <Button
                variant="primary"
                loading={saving}
                disabled={!changed || over}
                onClick={() => void save()}
              >
                Save keepers
              </Button>
              <Caption>
                {chosen.length} of {view.count} chosen
              </Caption>
            </Line>
          </Stack>
        ) : null}
      </div>
    </Panel>
  );
}

/** Every team's keepers, as chosen so far. */
function Everyone({ view }: { view: KeepersView }) {
  return (
    <Panel title="Every team’s keepers">
      <ul className="fsp-keepers-teams">
        {view.teams.map((t) => {
          const kept = t.players.filter((p) => p.kept);
          return (
            <li key={t.team.id} className="fsp-keepers-team">
              <TeamName team={t.team} />
              {kept.length === 0 ? (
                <Caption>Nobody yet</Caption>
              ) : (
                <ul className="fsp-keepers-kept">
                  {kept
                    .sort((a, b) => (a.round ?? 0) - (b.round ?? 0))
                    .map((p) => (
                      <li key={p.player.id}>
                        <Text size="xs">{p.player.name}</Text>{' '}
                        <Caption>
                          {p.cost?.price !== null && p.cost?.price !== undefined
                            ? `$${p.cost.price}`
                            : p.round !== null
                              ? `Round ${p.round}`
                              : ''}
                        </Caption>
                      </li>
                    ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
